import { createFileRoute, useRouteContext } from "@tanstack/react-router";
import { graphql, useLazyLoadQuery, useRefetchableFragment } from "react-relay";
import {
	ApiKeysTable,
	type ApiKeyTableDef,
} from "@/components/tables/api-keys";
import { Button } from "@/components/ui/button";
import type { apiKeys_apiKeys$key } from "./__generated__/apiKeys_apiKeys.graphql";
import type { apiKeysQuery } from "./__generated__/apiKeysQuery.graphql";

export const Route = createFileRoute(
	"/_logged/c/$club/(sidebar)/(admins)/configuracoes/api-keys",
)({
	component: RouteComponent,
});

function ApiKeysList({ apiKeysRef }: { apiKeysRef: apiKeys_apiKeys$key }) {
	const [data, refetch] = useRefetchableFragment(
		graphql`
			fragment apiKeys_apiKeys on Club
			@refetchable(queryName: "apiKeysRefetchQuery")
			@argumentDefinitions(
				first: { type: "Int", defaultValue: 10 }
				last: { type: "Int" }
				after: { type: "String" }
				before: { type: "String" }
			) {
				apiKeys(first: $first, last: $last, after: $after, before: $before) {
					edges {
						node {
							id
							rawID
							name
							key
							createdAt
							createdBy {
								displayName
							}
						}
					}
					pageInfo {
						hasNextPage
						hasPreviousPage
						startCursor
						endCursor
					}
				}
			}
		`,
		apiKeysRef,
	);

	const apiKeys = data.apiKeys.edges
		.filter(Boolean)
		.map(({ node }) => node) as unknown as ApiKeyTableDef[];

	const refresh = () => {
		refetch({}, { fetchPolicy: "network-only" });
	};
	const pageInfo = data.apiKeys.pageInfo;

	return (
		<div className="space-y-6">
			<ApiKeysTable data={apiKeys} onDeleted={refresh} />
			<div className="flex justify-end gap-2">
				<Button
					variant="outline"
					disabled={!pageInfo.hasPreviousPage}
					onClick={() =>
						refetch(
							{
								first: null,
								last: 10,
								after: null,
								before: pageInfo.startCursor,
							},
							{ fetchPolicy: "network-only" },
						)
					}
				>
					Anterior
				</Button>
				<Button
					variant="outline"
					disabled={!pageInfo.hasNextPage}
					onClick={() =>
						refetch(
							{
								first: 10,
								last: null,
								after: pageInfo.endCursor,
								before: null,
							},
							{ fetchPolicy: "network-only" },
						)
					}
				>
					Próxima
				</Button>
			</div>
		</div>
	);
}

function RouteComponent() {
	const { club } = useRouteContext({ from: "/_logged/c/$club" });

	const data = useLazyLoadQuery<apiKeysQuery>(
		graphql`
			query apiKeysQuery($slug: String!) {
				club(slug: $slug) {
					id
					...apiKeys_apiKeys
				}
			}
		`,
		{ slug: club.slug },
	);

	return (
		<div className="p-6">
			<ApiKeysList apiKeysRef={data.club} />
		</div>
	);
}
